/**
 * Secrets Collective — same-page anchors without the hash in the address bar.
 *
 * Three links put a fragment in the URL: the skip-to-content link (#MainContent)
 * and the two product page jumps (#models, #sizechart). Customers saw
 * secretscollective.co/#MainContent and it reads like a broken link.
 *
 * The links themselves stay — the skip link in particular is how a keyboard or
 * screen reader user gets past the header, and deleting it to tidy a URL would
 * trade a real accessibility feature for a cosmetic one. Instead this takes over
 * the navigation: scroll to the target, move focus there so assistive tech
 * follows, and leave the address bar alone.
 *
 * Nothing in the theme reads location.hash, so there is no behaviour to break.
 */

const ANCHOR = 'a[href^="#"]:not([href="#"])';

/**
 * Focus without a permanent tabindex. Headings and <main> are not focusable by
 * default, so the attribute goes on for the move and comes off afterwards —
 * otherwise every jump target joins the tab order for the rest of the session.
 */
function focusTarget(el) {
  const hadTabindex = el.hasAttribute('tabindex');
  if (!hadTabindex) el.setAttribute('tabindex', '-1');

  el.focus({ preventScroll: true });

  if (!hadTabindex) {
    el.addEventListener('blur', () => el.removeAttribute('tabindex'), { once: true });
  }
}

document.addEventListener('click', (event) => {
  const link = event.target.closest?.(ANCHOR);
  if (!link) return;

  // Leave modified clicks alone — ctrl/cmd-click opens a new tab, and that
  // tab does want the fragment so it lands in the right place.
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const id = decodeURIComponent(link.getAttribute('href').slice(1));
  const target = document.getElementById(id) || document.getElementsByName(id)[0];
  if (!target) return; // a dead anchor is better left to the browser

  event.preventDefault();

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  focusTarget(target);
});

/*
 * A link shared or bookmarked with a fragment still arrives with one. Let the
 * browser do its jump first, then tidy the URL — replaceState does not scroll,
 * so the reader stays where they landed.
 */
if (window.location.hash) {
  window.addEventListener(
    'load',
    () => {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    },
    { once: true }
  );
}
